/**
 * Descriptions for each field in the PropertyReport interface.
 * These descriptions explain what information should be included in each field
 * and how it should be interpreted.
 */
export const propertyReportFieldDescriptions = {
    /** Property Overview Section */
    propertyOverview: {
        buildingName: {
            description: "The official or common name of the building, if different from the address.",
            isAnalysisField: false,
        },
        ageOfBuilding: {
            description: "The age of the building in years (round to the nearist year) calculated from the year of the the completion date.",
            isAnalysisField: false,
        },
        constructionType: {
            description: "The primary construction material and method (e.g., concrete high-rise, wood-frame low-rise).",
            isAnalysisField: false,
        },
        numberOfUnits: {
            description: "The total number of strata lots in the development.",
            isAnalysisField: false,
        },
        numberOfFloors: {
            description: "The total number of floors or levels in the building. If appropriate, mention if the floors are above grade or below grade.",
            isAnalysisField: false,
        },
    },
    /** Financial Information Section */
    financialInformation: {
        monthlyStrataFees: {
            description: "Current strata fees charged monthly to owners. May vary by unit size/type.",
            isAnalysisField: false,
        },
        contingencyReserveFund: {
            currentBalance: {
                description: "The current balance in the Contingency Reserve Fund (CRF) as of the most recent financial statement.",
                isAnalysisField: false,
            },
            recentExpenditures: {
                description: "Significant recent withdrawals from the CRF, including purpose and amounts.",
                isAnalysisField: true,
            },
            anticipatedFutureSpending: {
                description: "Planned or anticipated future expenses to be paid from the CRF. Only include spending that is planned AFTER the current date.",
                isAnalysisField: true,
            },
            analysis: {
                description: "Assessment of CRF adequacy, including likelihood of special levies or fee increases based on planned expenses.",
                isAnalysisField: false,
            },
        },
        specialLevies: {
            description: "Any special levies currently in effect, including purpose, total amount, and payment schedule. Observation should clear, to the point and concise. Use bullet points if necessary.",
            isAnalysisField: true,
        },
    },
    /** Building Condition Section */
    buildingCondition: {
        buildingComponents: {
            description: `A list of each major building component, including age, expected lifespan, recent repairs, expected replacements, and any identified issues.
        
        Structure this small report with bullet points.
        `,
            isAnalysisField: true,
        },
    },
    /** Rules and Restrictions Section */
    rulesAndRestrictions: {
        petPolicy: {
            description: `Current rules for unit residents regarding pets, including restrictions on type, size, number, or specific breeds. Observation should clear, to the point and very concise.
      
      e.g.
        Max 2 birds, 1 cat or dog, or small caged pets (no exotics). Wildlife feeding banned. \$200 fine for issues. Leash pets outside. Register with council after approval.
      `,
            isAnalysisField: false,
        },
        rentalRestrictions: {
            description: "Rules governing rental of units, including any caps on number of rentals or minimum lease terms.",
            isAnalysisField: false,
        },
        smokingRestrictions: {
            description: "Policies regarding smoking within units, on balconies, and in common areas.",
            isAnalysisField: false,
        },
        otherRestrictions: {
            description: "Additional notable rules affecting daily living or property use (e.g., BBQ rules, noise regulations).",
            isAnalysisField: false,
        },
    },
    /** Amenities Section */
    amenities: {
        availableFacilities: {
            description: "List of common facilities and amenities available to residents.",
            isAnalysisField: false,
        },
        parkingAndStorage: {
            description: "Details about parking availability, types (owned/rental), and storage locker information.",
            isAnalysisField: false,
        },
        bicycleStorage: {
            description: "Information about bicycle storage facilities, including availability and any associated fees.",
            isAnalysisField: false,
        },
    },
    /** Insurance Summary Section */
    insuranceSummary: {
        provider: {
            description: "Name of the strata's current insurance provider.",
            isAnalysisField: false,
        },
        coverageSummary: {
            description: "Overview of major coverages included in the strata insurance policy.",
            isAnalysisField: true,
        },
        ownerInsuranceRequired: {
            description: "Whether individual unit owners are required to maintain their own insurance.",
            isAnalysisField: false,
        },
        deductible: {
            description: "Current insurance deductibles for various types of claims (water damage, fire, etc.).",
            isAnalysisField: false,
        },
    },
    /** Community and Management Section */
    communityAndManagement: {
        strataManagementCompanyInfo: {
            name: {
                description: "Name of the property management company.",
                isAnalysisField: false,
            },
            contact: {
                description: "Primary contact information for property management. Use markdown formatting, bullets, if there is multiple contact details.",
                isAnalysisField: false,
            },
            notes: {
                description: "Observations about management company's performance, responsiveness, and reputation.",
                isAnalysisField: true,
            },
        },
        councilInfo: {
            numberOfMembers: {
                description: "Current number of strata council members.",
                isAnalysisField: false,
            },
            meetingFrequency: {
                description: "How often the strata council typically meets.",
                isAnalysisField: false,
            },
            currentInitiatives: {
                description: "Major projects or initiatives being pursued by the current council. Focus on RECENT initiatives, especially those mentioned in recent meeting minutes.",
                isAnalysisField: true,
            },
        },
    },
    /** Local Area Insights Section */
    localAreaInsights: {
        neighbourhoodHighlights: {
            description: "Notable features of the area, including amenities, transit, schools, and development plans.",
            isAnalysisField: true,
        },
        recentComparableSales: {
            description: "Recent sales data for similar units in the building or comparable nearby properties.",
            isAnalysisField: true,
        },
    },
    /** Additional Notes Section */
    additionalNotes: {
        potentialBuyerConcerns: {
            description: "Common questions or concerns raised by potential buyers about this property.",
            isAnalysisField: true,
        },
        uniqueSellingPoints: {
            description: "Distinctive features or characteristics that make this property particularly attractive.",
            isAnalysisField: true,
        },
    },
};
//# sourceMappingURL=propertyReportFieldDescriptions.js.map